import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import logger from './utils/logger';

/**
 * Hook para buscar histórico de números de série baseado no perfil do usuário
 * @param {string} userId - ID do usuário autenticado
 * @param {string} userRole - Papel do usuário (master ou regional)
 * @param {Array} userRegionals - Regionais do usuário
 * @returns {Object} - Histórico, loading, função de refresh e exportação
 */
const useSerialHistory = (userId, userRole, userRegionals) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    /**
     * Busca o histórico de números de série baseado no perfil
     */
    const fetchHistory = useCallback(async () => {
        if (!userId) return;

        setLoading(true);
        setError('');

        try {
            let query = supabase
                .from('serial_numbers')
                .select(`
                    id,
                    serial_number,
                    created_at,
                    profiles:user_id (
                        email
                    )
                `)
                .order('created_at', { ascending: false });

            // Aplicar filtros baseados no papel do usuário
            if (userRole === 'master') {
                // Master vê todos os NS
                logger.debug('Usuário master: carregando histórico completo');
                // Não aplica nenhum filtro - busca todos os registros
            } else {
                // Usuário não-master só pode ver NS que ele próprio gerou
                logger.debug('Usuário regional: carregando histórico próprio');
                query = query.eq('user_id', userId);
            }

            const { data, error } = await query;

            if (error) {
                logger.error('Erro ao buscar histórico:', error.message);
                setError('Erro ao carregar o histórico de números de série.');
                return;
            }

            logger.debug(`Histórico carregado: ${data?.length || 0} registros`);
            setHistory(data || []);

        } catch (err) {
            logger.error('Erro inesperado ao carregar histórico:', err.message);
            setError('Erro inesperado ao carregar histórico.');
        } finally {
            setLoading(false);
        }
    }, [userId, userRole]);

    /**
     * Exporta o histórico para CSV
     */
    const exportHistory = useCallback(() => {
        if (!history.length) {
            return { success: false, message: 'Nenhum registro para exportar.' };
        }

        try {
            // Cabeçalho do CSV
            const headers = ['Número de Série', 'Data de Criação', 'Usuário'];

            // Dados do CSV
            const csvData = history.map(item => [
                item.serial_number,
                new Date(item.created_at).toLocaleString('pt-BR'),
                item.profiles?.email || 'N/A'
            ]);

            // Combinar cabeçalho com dados
            const csvContent = [headers, ...csvData]
                .map(row => row.map(field => `"${field}"`).join(','))
                .join('\n');

            // Criar e baixar arquivo
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.setAttribute('href', url);
            link.setAttribute('download', `historico_ns_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            return { success: true, message: 'Histórico exportado com sucesso!' };

        } catch (error) {
            logger.error('Erro ao exportar histórico:', error.message);
            return { success: false, message: 'Erro ao exportar histórico.' };
        }
    }, [history]);

    // Buscar histórico quando as dependências mudarem
    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    return {
        history,
        loading,
        error,
        refreshHistory: fetchHistory,
        exportHistory
    };
};

export default useSerialHistory;